export enum SurveyType {
  PreEvent = 'pre_event',
  PostEvent = 'post_event',
}

export enum SurveyMode {
  BuiltIn = 'built_in',
  ExternalLink = 'external_link',
}

export enum SurveyQuestionType {
  Rating = 'rating',
  FreeText = 'free_text',
}

export interface RatingQuestionConfig {
  min: number
  max: number
  minLabel: string // e.g., "Not satisfied"
  maxLabel: string // e.g., "Very satisfied"
}

export interface BaseSurveyQuestion {
  id?: string // optional - API assigns ID on creation, user uses it for editing
  type: SurveyQuestionType
  label: string
  required: boolean
}

export interface RatingQuestion extends BaseSurveyQuestion {
  type: SurveyQuestionType.Rating
  config: RatingQuestionConfig
}

export interface FreeTextQuestion extends BaseSurveyQuestion {
  type: SurveyQuestionType.FreeText
  placeholder?: string
}

export type SurveyQuestion = RatingQuestion | FreeTextQuestion

export type SurveyQuestionWithId = SurveyQuestion & { id: string }

/** Question with edit context from GET survey (owner view) */
export type SurveyQuestionWithEditMeta = SurveyQuestionWithId & {
  active?: boolean
  responseCount?: number
  editable?: boolean
  disabledAt?: string
}

export interface BaseSurveyConfig {
  enabled: boolean
  mode: SurveyMode
}

export interface BuiltInSurveyConfig extends BaseSurveyConfig {
  mode: SurveyMode.BuiltIn
  questions: SurveyQuestion[]
}

export interface BuiltInSurveyConfigResponse extends BaseSurveyConfig {
  mode: SurveyMode.BuiltIn
  questions: (SurveyQuestionWithId | SurveyQuestionWithEditMeta)[]
  id?: string
}

export interface ExternalLinkSurveyConfig extends BaseSurveyConfig {
  mode: SurveyMode.ExternalLink
  externalUrl: string
}

export interface ExternalLinkSurveyConfigResponse
  extends ExternalLinkSurveyConfig {
  id?: string
}

export type SurveyConfig = BuiltInSurveyConfig | ExternalLinkSurveyConfig

export interface BuiltInSurveyConfigEdit extends BuiltInSurveyConfig {
  id: string
}

export interface ExternalLinkSurveyConfigEdit extends ExternalLinkSurveyConfig {
  id: string
}

export type SurveyConfigEdit =
  | BuiltInSurveyConfigEdit
  | ExternalLinkSurveyConfigEdit

export type SurveyConfigResponse =
  | BuiltInSurveyConfigResponse
  | ExternalLinkSurveyConfigResponse

export interface SurveyAccess {
  canTakeSurvey: boolean
}

export type SurveyConfigWithType = SurveyConfigResponse & {
  surveyType: SurveyType
}

export interface GetOpportunitySurveyResponse {
  access: SurveyAccess
  surveys: SurveyConfigWithType[]
}

// Form state (UI) - holds both values to preserve data when switching modes
export interface SurveyFormState {
  enabled: boolean
  mode: SurveyMode
  questions: SurveyQuestion[]
  externalUrl: string
}

export type SurveyDto = {
  id: string
  opportunityId: string
  enabled: boolean
  mode: string
  questions?: SurveyQuestionWithId[]
  externalUrl?: string
}

export type EditOpportunitySurveyParams = {
  opportunityId: string
  survey: SurveyFormState
  surveyId?: string
  surveyType: SurveyType
}
